import { GeoCoordinate, GeoSearch, MILES_TO_METERS } from '@aloft/toolkit-shared';

import { proximityMiles } from './proximityMiles.ts';

export type LocationType = 'Urban' | 'Suburban' | 'Rural';

export function getLocationTypeProximityRequirementMiles(
  locationType: LocationType | undefined,
): number | undefined {
  if (!locationType) return undefined;
  switch (locationType) {
    case 'Urban':
      return 0.5;
    case 'Suburban':
      return 1;
    case 'Rural':
      return 10;
    default:
      return undefined;
  }
}

function formatMiles(miles: number): string {
  if (miles === 1 || miles < 1) return `${miles} mile`;
  return `${miles} miles`;
}

export { formatMiles };

export function formatProximityRequirementTooltip(
  locationType: LocationType,
  requirementMiles: number,
): string {
  return `Exceeds ${formatMiles(requirementMiles)} suggested for ${locationType} locations`;
}

export function formatProximityRequirementRadiusTooSmall(
  locationType: LocationType,
  requirementMiles: number,
): string {
  return `For ${locationType} locations, properties in a ${requirementMiles} mi radius are recommended (in green).`;
}

export function formatProximityRequirementPolygonTooSmall(
  locationType: LocationType,
  requirementMiles: number,
): string {
  return `Your selection is outside the recommended ${formatMiles(requirementMiles)} for ${locationType} locations.`;
}

const polygonExceedsProximityRequirement = ({
  coords,
  subjectLat,
  subjectLng,
  proximityRequirementMiles,
}: {
  coords: GeoCoordinate[];
  subjectLat: number;
  subjectLng: number;
  proximityRequirementMiles: number;
}): boolean =>
  coords.some(([lng, lat]) => {
    const miles = proximityMiles(
      { latitude: subjectLat, longitude: subjectLng },
      { latitude: lat, longitude: lng },
    );
    return miles !== undefined && miles > proximityRequirementMiles;
  });

export const searchExceedsProximityRequirement = ({
  geoSearch,
  subjectLat,
  subjectLng,
  proximityRequirementMiles,
}: {
  geoSearch: GeoSearch;
  subjectLat: number;
  subjectLng: number;
  proximityRequirementMiles: number;
}): boolean => {
  if (geoSearch.t === 'radius') {
    return geoSearch.radiusInMeters > proximityRequirementMiles * MILES_TO_METERS;
  }
  return (
    geoSearch.coords.length > 0 &&
    polygonExceedsProximityRequirement({
      coords: geoSearch.coords,
      subjectLat,
      subjectLng,
      proximityRequirementMiles,
    })
  );
};

